import { useState, useEffect, useContext, createContext } from "react";
import api from "../services/api";
import * as Sentry from "@sentry/react";
import { useVisibility } from "./useVisibility";

export const authContext = createContext();

function useAuthProvider() {
  const [token, setToken] = useState();
  const [user, setUser] = useState();
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const { setPinnedTabs } = useVisibility();

  const privilegeLabels = {
    admin: {
      admin: {
        label: "Admin",
        description: "Full administrative access to all system features and settings.",
      },
    },
    settings: {
      editRoles: {
        label: "Edit Roles",
        description: "Can create, update, or delete user roles and their associated privileges.",
      },
      editUser: {
        label: "Edit User",
        description: "Can create, update, or assign roles to user accounts.",
      },
    },
  };

  // Flatten privilegeLabels to a map: { privilegeKey: { label, description } }
  const privilegeLabelsMap = Object.values(privilegeLabels).reduce((acc, curr) => {
    Object.entries(curr).forEach(([key, value]) => {
      acc[key] = value;
    });
    return acc;
  }, {});

  const [employeeNames, setEmployeeNames] = useState([]);

  useEffect(() => {
    const fetchEmployeeNames = async () => {
      const response = await api.getEmployeeNames();
      if (response.status === 200) {
        setEmployeeNames(response.data);
      }
    };

    if (user) {
      fetchEmployeeNames();
    }
  }, [user]);

  const login = async (credentialResponse) => {
    let result = await api.login(credentialResponse);

    if (result.status === 200) {
      checkAuth();
      return true;
    } else {
      if (result.problem === "NETWORK_ERROR") {
        setShowNetworkError(true);
      }
      return false;
    }
  };

  const logout = async () => {
    await api.logout();

    setToken(null);
    setUser(null);
  };

  const checkAuth = async () => {
    setIsConnecting(true);
    const response = await api.validateToken();

    if (response.status === 200 && response.data) {
      setUser(response.data);
      Sentry.setUser({
        email: response.data.email,
        id: response.data.id,
      });

      if (response.data.pinnedTabs && Array.isArray(response.data.pinnedTabs)) {
        setPinnedTabs(response.data.pinnedTabs);
      }
    } else {
      if (response.problem === "NETWORK_ERROR") {
        setShowNetworkError(true);
      }
    }
    setIsConnecting(false);
  };

  const checkPrivileges = (action) => {
    if (user?.Role.privileges.indexOf("admin") !== -1) {
      return true;
    }

    if (user?.Role.privileges.indexOf(action) !== -1) {
      return true;
    }

    return false;
  };

  return {
    token,
    user,
    setToken,
    checkAuth,
    logout,
    login,
    checkPrivileges,
    employeeNames,
    setEmployeeNames,
    privilegeLabels,
    privilegeLabelsMap,
    showNetworkError,
    setShowNetworkError,
    isConnecting,
  };
}

export function ProvideAuth({ children }) {
  return <authContext.Provider value={useAuthProvider()}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};
